/**
 * Minimal, dependency-free JWT helpers. The add-in only ever needs to read a
 * couple of unverified claims (expiry, email/tenant) from tokens MSAL already
 * validated — never to verify a signature — so a tiny base64url decoder is
 * enough and avoids pulling in a JWT library.
 */

/** Decode a JWT payload (the middle segment) into a claims object, or `{}`. */
export function decodeJwtClaims(token: string): Record<string, unknown> {
  try {
    const payload = token.split(".")[1];
    if (!payload) return {};
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * The token's `exp` claim as an epoch-ms timestamp, or `null` when the token is
 * unparseable or carries no numeric `exp`. JWT `exp` is in **seconds**, so we
 * scale to ms to match `Date.now()` / MSAL `expiresOn.getTime()`.
 */
export function decodeJwtExpiryMs(token: string | null | undefined): number | null {
  if (!token) return null;
  const exp = decodeJwtClaims(token).exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : null;
}

/**
 * The signed-in user's email/UPN from a JWT (normally the id token), or `null`.
 * IdP-agnostic: probes the standard OIDC / Entra claim names in preference order
 * — `email` (generic OIDC / Cognito), then `preferred_username`, `upn`,
 * `unique_name` (Entra) — and only accepts a value that looks like an
 * address (`@`). Used to stamp the Studio Logs page's User column for add-in
 * telemetry, where the API access token has been stripped of these claims.
 */
export function emailFromJwt(token: string | null | undefined): string | null {
  if (!token) return null;
  const claims = decodeJwtClaims(token);
  for (const key of ["email", "preferred_username", "upn", "unique_name"] as const) {
    const value = claims[key];
    if (typeof value === "string" && value.includes("@")) {
      return value.trim().toLowerCase();
    }
  }
  return null;
}
